/**
 * useTenantFeatureFlags — Reads per-tenant feature flags from the backend.
 *
 * Flags are seeded from the `features:` section of
 * `common/configs/seed/constellations/constellation-facility-meta` config.
 *
 * To enable/disable a feature for a tenant: update the config `features:` section
 * and re-run ferp-seed. No code changes required.
 *
 * DRR-055: Frontend NEVER hardcodes which features to show. This hook is the
 * single source of truth for conditional feature rendering.
 */

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';

// ---------------------------------------------------------------------------
// GraphQL
// ---------------------------------------------------------------------------

const TENANT_FEATURE_FLAGS_QUERY = gql`
  query TenantFeatureFlags {
    formsTaskTenantFeatureFlags {
      careJourney
      beaconDashboard
      witnessQueue
      standardsPack
      qapiDashboard
      syncDashboard
    }
  }
`;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TenantFeatureFlags {
  careJourney: boolean;
  beaconDashboard: boolean;
  witnessQueue: boolean;
  standardsPack: boolean;
  qapiDashboard: boolean;
  syncDashboard: boolean;
}

interface QueryData {
  formsTaskTenantFeatureFlags: TenantFeatureFlags;
}

/** All features enabled — used while loading or on error (fail-open). */
const DEFAULT_FLAGS: TenantFeatureFlags = {
  careJourney: true,
  beaconDashboard: true,
  witnessQueue: true,
  standardsPack: true,
  qapiDashboard: true,
  syncDashboard: true,
};

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface TenantFeatureFlagsResult {
  flags: TenantFeatureFlags;
  loading: boolean;
}

/**
 * Returns the current tenant's feature flags.
 *
 * Fails-open: if the query is loading or errors, all features are enabled.
 * This prevents the UI from hiding features due to a transient network issue.
 */
export function useTenantFeatureFlags(): TenantFeatureFlagsResult {
  const { data, loading } = useQuery<QueryData>(TENANT_FEATURE_FLAGS_QUERY, {
    fetchPolicy: 'cache-first',
    // Fetch once per session — flags change only after a re-seed.
    nextFetchPolicy: 'cache-only',
  });

  return {
    flags: data?.formsTaskTenantFeatureFlags ?? DEFAULT_FLAGS,
    loading,
  };
}
